import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  adminApi,
  type AdminActivity,
  type AdminFavorite,
  type AdminGroup,
  type AdminShare,
  type AdminUserDetail,
} from "@/api/admin";
import { ApiError } from "@/api/client";
import { toast } from "@/components/Toast";

type TabKey = "info" | "favorites" | "shares-to" | "shares-by" | "groups" | "activity";

const TABS: { key: TabKey; label: string }[] = [
  { key: "info", label: "概览" },
  { key: "favorites", label: "收藏" },
  { key: "shares-to", label: "收到的共享" },
  { key: "shares-by", label: "发出的共享" },
  { key: "groups", label: "分组" },
  { key: "activity", label: "操作日志" },
];

export default function UserDetailPage() {
  const { openId = "" } = useParams<{ openId: string }>();
  const [tab, setTab] = useState<TabKey>("info");
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reloadDetail = useCallback(() => {
    setError(null);
    adminApi
      .userDetail(openId)
      .then(setDetail)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      );
  }, [openId]);

  useEffect(() => {
    reloadDetail();
  }, [reloadDetail]);

  const onRevoke = async () => {
    const reason = window.prompt("撤销原因（可选）：");
    if (reason === null) return;
    try {
      await adminApi.revokeToken(openId, reason || null);
      toast.success("Token 已撤销");
      reloadDetail();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm">
        <Link to="/admin/users" className="text-slate-500 hover:underline">
          ← 用户列表
        </Link>
        <span className="font-mono text-xs text-slate-400">{openId}</span>
      </div>

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 pb-2 text-xs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-3 py-1 ${
              tab === t.key
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && detail ? (
        <InfoTab detail={detail} onRevoke={onRevoke} />
      ) : null}
      {tab === "favorites" ? <FavoritesTab openId={openId} /> : null}
      {tab === "shares-to" ? (
        <SharesTab openId={openId} direction="to" />
      ) : null}
      {tab === "shares-by" ? (
        <SharesTab openId={openId} direction="by" />
      ) : null}
      {tab === "groups" ? <GroupsTab openId={openId} /> : null}
      {tab === "activity" ? <ActivityTab openId={openId} /> : null}
    </div>
  );
}

function InfoTab({
  detail,
  onRevoke,
}: {
  detail: AdminUserDetail;
  onRevoke: () => void;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">账户</h3>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-slate-500">姓名</dt>
          <dd>{detail.name || "—"}</dd>
          <dt className="text-slate-500">open_id</dt>
          <dd className="font-mono text-xs">{detail.open_id}</dd>
          <dt className="text-slate-500">union_id</dt>
          <dd className="font-mono text-xs">{detail.union_id || "—"}</dd>
          <dt className="text-slate-500">注册时间</dt>
          <dd>{new Date(detail.created_at).toLocaleString("zh-CN")}</dd>
          <dt className="text-slate-500">access 到期</dt>
          <dd className="text-xs">
            {new Date(detail.access_expires_at).toLocaleString("zh-CN")}
          </dd>
          <dt className="text-slate-500">refresh 到期</dt>
          <dd className="text-xs">
            {new Date(detail.refresh_expires_at).toLocaleString("zh-CN")}
            {detail.refresh_revoked ? (
              <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700">
                已撤销
              </span>
            ) : null}
          </dd>
        </dl>

        <button
          onClick={onRevoke}
          disabled={detail.refresh_revoked}
          className="mt-4 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-100 disabled:opacity-50"
        >
          {detail.refresh_revoked ? "已撤销" : "撤销 Token"}
        </button>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold">数据</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Counter label="收藏数" value={detail.counters.favorites} />
          <Counter label="收到共享" value={detail.counters.shared_to_me} />
          <Counter label="发出共享" value={detail.counters.shared_by_me} />
          <Counter label="分组数" value={detail.counters.groups} />
          <Counter label="转发记录" value={detail.counters.forwards} />
        </div>
      </section>
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function useCursorPage<T>(
  fetcher: (cursor: number | null) => Promise<{ items: T[]; next_cursor: number | null }>,
) {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    fetcher(null)
      .then((r) => {
        setItems(r.items);
        setCursor(r.next_cursor);
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  }, [fetcher]);

  useEffect(() => reload(), [reload]);

  const loadMore = () => {
    if (!cursor || loading) return;
    setLoading(true);
    fetcher(cursor)
      .then((r) => {
        setItems((prev) => [...prev, ...r.items]);
        setCursor(r.next_cursor);
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  };

  return { items, cursor, loading, error, loadMore, reload };
}

function FavoritesTab({ openId }: { openId: string }) {
  const fetcher = useMemo(
    () => (c: number | null) => adminApi.userFavorites(openId, c),
    [openId],
  );
  const { items, cursor, loading, error, loadMore, reload } =
    useCursorPage<AdminFavorite>(fetcher);

  const onDelete = async (id: number) => {
    const reason = window.prompt("强删原因（可选）：");
    if (reason === null) return;
    if (!confirm(`强删收藏 #${id}？将级联清理共享行。`)) return;
    try {
      await adminApi.forceDeleteFavorite(id, reason || null);
      toast.success(`已删除收藏 #${id}`);
      reload();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <ListShell error={error} loading={loading} cursor={cursor} loadMore={loadMore}>
      {items.map((f) => (
        <div
          key={f.id}
          className="rounded-lg border border-slate-200 bg-white p-3 text-sm"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-slate-500">
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px]">
                #{f.id}
              </span>{" "}
              <span className="rounded bg-slate-100 px-1.5 py-0.5">
                {f.msg_type}
              </span>{" "}
              {new Date(f.created_at).toLocaleString("zh-CN")}
            </div>
            <button
              onClick={() => onDelete(f.id)}
              className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700 hover:bg-rose-100"
            >
              强删
            </button>
          </div>
          <div className="mt-1 line-clamp-2 text-slate-700">
            {f.content_text || "—"}
          </div>
        </div>
      ))}
    </ListShell>
  );
}

function SharesTab({
  openId,
  direction,
}: {
  openId: string;
  direction: "to" | "by";
}) {
  const fetcher = useMemo(
    () => (c: number | null) => adminApi.userShares(openId, direction, c),
    [openId, direction],
  );
  const { items, cursor, loading, error, loadMore, reload } =
    useCursorPage<AdminShare>(fetcher);

  const onUnshare = async (id: number) => {
    const reason = window.prompt("强解共享原因（可选）：");
    if (reason === null) return;
    if (!confirm(`强解共享 #${id}？`)) return;
    try {
      await adminApi.forceUnshare(id, reason || null);
      toast.success(`已解除 #${id}`);
      reload();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <ListShell error={error} loading={loading} cursor={cursor} loadMore={loadMore}>
      {items.map((s) => (
        <div
          key={s.id}
          className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm"
        >
          <div className="min-w-0">
            <div className="text-xs text-slate-500">
              #{s.id} · fav={s.favorite_id}
            </div>
            <div className="font-mono text-xs">
              {s.shared_by} → {s.shared_to}
              {s.via_group_id ? ` (group=${s.via_group_id})` : ""}
            </div>
          </div>
          <button
            onClick={() => onUnshare(s.id)}
            className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700 hover:bg-rose-100"
          >
            强解
          </button>
        </div>
      ))}
    </ListShell>
  );
}

function GroupsTab({ openId }: { openId: string }) {
  const [items, setItems] = useState<AdminGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi
      .userGroups(openId)
      .then((r) => setItems(r.items))
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      );
  }, [openId]);

  if (error)
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        {error}
      </div>
    );
  if (items.length === 0)
    return <div className="py-6 text-center text-sm text-slate-400">无分组</div>;

  return (
    <div className="space-y-2">
      {items.map((g) => (
        <div
          key={g.id}
          className="rounded-lg border border-slate-200 bg-white p-3 text-sm"
        >
          <div className="font-medium">
            {g.name}{" "}
            <span className="text-xs text-slate-400">({g.members.length} 人)</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {g.members.map((m) => (
              <span
                key={m.open_id}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
              >
                {m.name || m.open_id}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityTab({ openId }: { openId: string }) {
  const fetcher = useMemo(
    () => (c: number | null) => adminApi.userActivity(openId, c),
    [openId],
  );
  const { items, cursor, loading, error, loadMore } =
    useCursorPage<AdminActivity>(fetcher);

  return (
    <ListShell error={error} loading={loading} cursor={cursor} loadMore={loadMore}>
      {items.map((a) => (
        <div
          key={a.id}
          className="rounded-lg border border-slate-200 bg-white p-3 text-xs"
        >
          <div className="flex items-center gap-2">
            <span
              className={`rounded px-1.5 py-0.5 ${
                a.status === "success"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-rose-100 text-rose-700"
              }`}
            >
              {a.status}
            </span>
            <span className="text-slate-500">
              {a.target_type} → {a.target_id}
            </span>
            <span className="ml-auto text-slate-400">
              {new Date(a.created_at).toLocaleString("zh-CN")}
            </span>
          </div>
          {a.error ? (
            <div className="mt-1 text-rose-700">{a.error}</div>
          ) : null}
        </div>
      ))}
    </ListShell>
  );
}

function ListShell({
  children,
  error,
  loading,
  cursor,
  loadMore,
}: {
  children: React.ReactNode;
  error: string | null;
  loading: boolean;
  cursor: number | null;
  loadMore: () => void;
}) {
  return (
    <div className="space-y-2">
      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {children}
      {cursor ? (
        <div className="text-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs text-slate-600 disabled:opacity-50"
          >
            {loading ? "加载中…" : "加载更多"}
          </button>
        </div>
      ) : null}
      {!loading && !cursor && (children as unknown as unknown[])?.toString() === "" ? (
        <div className="py-6 text-center text-sm text-slate-400">无数据</div>
      ) : null}
    </div>
  );
}
